import { Tooltip } from "antd";
import { ManagePlanQuantityStepper } from "./ManagePlanQuantityStepper";
import { ManagePlanUsageBar } from "./ManagePlanUsageBar";
import { MOCK_DEPLOYED_APP_PRICE_MONTHLY_USD } from "./mockData";
import type { BillingInterval } from "store/slices/reduxApi/billing";

type ManagePlanDeployPanelProps = {
  billingInterval: BillingInterval;
  deployedAppsIncluded: number;
  currentDeployedApps: number;
  currentDeployedAppsUsage: number;
  deployedAppCountUnavailable?: boolean;
  deployedApps: number;
  onDeployedAppsChange: (value: number) => void;
};

function formatUsd(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

function AppsIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 text-gray-500"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}

export function ManagePlanDeployPanel({
  billingInterval,
  deployedAppsIncluded,
  currentDeployedApps,
  currentDeployedAppsUsage,
  deployedAppCountUnavailable = false,
  deployedApps,
  onDeployedAppsChange,
}: ManagePlanDeployPanelProps) {
  const deployedAppPrice =
    billingInterval === "annual"
      ? MOCK_DEPLOYED_APP_PRICE_MONTHLY_USD * 12
      : MOCK_DEPLOYED_APP_PRICE_MONTHLY_USD;
  const intervalSuffix = billingInterval === "annual" ? "/yr" : "/mo";
  const currentTotalLimit = deployedAppsIncluded + currentDeployedApps;
  const projectedTotalLimit = deployedAppsIncluded + deployedApps;
  const plural = (n: number) => (n === 1 ? "" : "s");
  const includedLine = `${deployedAppsIncluded} deployed app${plural(deployedAppsIncluded)} included with your plan`;
  const additionalLine = `${currentDeployedApps} additional deployed app${plural(currentDeployedApps)}`;
  const tooltipTitle = (
    <div>
      <div>{includedLine}</div>
      <div>{additionalLine}</div>
    </div>
  );

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50/60 px-4 py-3">
        <AppsIcon />
        <div className="text-sm font-semibold text-gray-950">Deployed Apps</div>
      </div>
      <div className="px-4 py-4">
        <div className="space-y-4">
          <div>
            <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-400">
              Current plan
            </div>
            <div className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-gray-950">
              <span>
                {currentTotalLimit} Deployed App{plural(currentTotalLimit)}
              </span>
              <Tooltip title={tooltipTitle} placement="top">
                <span className="flex h-4 w-4 cursor-help items-center justify-center rounded-full bg-gray-200 text-[10px] font-bold leading-none text-gray-500">
                  i
                </span>
              </Tooltip>
            </div>
          </div>

          <ManagePlanUsageBar
            used={currentDeployedAppsUsage}
            total={projectedTotalLimit}
            colorClassName="bg-sky-500"
          />

          <div className="my-3 h-px bg-gray-200" />
          <span className="block text-sm text-gray-500">
            Add additional Deployed Apps ({formatUsd(deployedAppPrice)}/app
            {intervalSuffix})
          </span>
          <div className="w-full">
            <ManagePlanQuantityStepper
              value={deployedApps}
              min={Math.max(0, currentDeployedAppsUsage - deployedAppsIncluded)}
              disabled={deployedAppCountUnavailable}
              onChange={onDeployedAppsChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
